import React, { useState, useEffect } from "react";
import styles from "../metamask-error-recovery/MetaMaskErrorRecovery.module.scss";

interface WalletConflictWarningProps {
  onClose?: () => void;
}

const WalletConflictWarning: React.FC<WalletConflictWarningProps> = ({
  onClose,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [detectedWallets, setDetectedWallets] = useState<string[]>([]);
  const [isDismissedPermanently, setIsDismissedPermanently] = useState(false);

  useEffect(() => {
    // Check if user has permanently dismissed this warning
    const dismissed = localStorage.getItem("wallet-conflict-warning-dismissed");
    if (dismissed === "true") {
      setIsDismissedPermanently(true);
      return;
    }

    const handleWalletConflict = (event: CustomEvent) => {
      const { wallets } = event.detail;
      setDetectedWallets(wallets);
      setIsVisible(true);
    };

    window.addEventListener(
      "wallet-conflict-detected",
      handleWalletConflict as EventListener
    );

    return () => {
      window.removeEventListener(
        "wallet-conflict-detected",
        handleWalletConflict as EventListener
      );
    };
  }, []);

  const handleClose = () => {
    setIsVisible(false);
    onClose?.();
  };

  const handleDismissPermanently = () => {
    localStorage.setItem("wallet-conflict-warning-dismissed", "true");
    setIsDismissedPermanently(true);
    setIsVisible(false);
    onClose?.();
  };

  const handleDisableWallet = (wallet: string) => {
    const instructions: Record<string, string> = {
      Phantom: "Go to chrome://extensions/ → Find Phantom → Toggle off",
      MetaMask: "Go to chrome://extensions/ → Find MetaMask → Toggle off",
      Coinbase:
        "Go to chrome://extensions/ → Find Coinbase Wallet → Toggle off",
      Rabby: "Go to chrome://extensions/ → Find Rabby Wallet → Toggle off",
      Brave: "Go to brave://settings/wallet → Disable Brave Wallet",
      OKX: "Go to chrome://extensions/ → Find OKX Wallet → Toggle off",
    };

    alert(
      `To disable ${wallet}:\n${
        instructions[wallet] || "Check your browser extensions page"
      }`
    );
  };

  if (!isVisible || isDismissedPermanently || detectedWallets.length <= 1) {
    return null;
  }

  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <h3>Multiple Wallet Extensions Detected</h3>
          <button
            className={styles.closeButton}
            onClick={handleClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className={styles.content}>
          <div className={styles.errorIcon}>⚠️</div>

          <div className={styles.message}>
            <p>
              You have multiple wallet extensions installed that may conflict
              with each other:
            </p>

            <div className={styles.explanation}>
              <ul>
                {detectedWallets.map((wallet, index) => (
                  <li
                    key={index}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      margin: "8px 0",
                    }}
                  >
                    <span>
                      <strong>{wallet}</strong>
                    </span>
                    <button
                      onClick={() => handleDisableWallet(wallet)}
                      style={{
                        background: "#ff6b6b",
                        color: "white",
                        border: "none",
                        padding: "4px 8px",
                        borderRadius: "4px",
                        fontSize: "12px",
                        cursor: "pointer",
                      }}
                    >
                      Disable
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            <div className={styles.explanation}>
              <p>
                <strong>Why this matters:</strong>
              </p>
              <ul>
                <li>Multiple wallets can cause connection conflicts</li>
                <li>Phantom wallet may auto-popup unexpectedly</li>
                <li>One wallet may override another's functionality</li>
                <li>This can prevent successful wallet connections</li>
              </ul>
            </div>
          </div>

          <div className={styles.actions}>
            <button
              className={`${styles.button} ${styles.primary}`}
              onClick={handleDismissPermanently}
            >
              Got it, don't show again
            </button>

            <button
              className={`${styles.button} ${styles.secondary}`}
              onClick={handleClose}
            >
              Remind me later
            </button>
          </div>

          <div className={styles.tips}>
            <h4>Recommendations:</h4>
            <ul>
              <li>
                <strong>Keep only one wallet active</strong> - Disable others
                temporarily
              </li>
              <li>
                <strong>Use different browsers</strong> - e.g., Chrome for
                MetaMask, Firefox for Phantom
              </li>
              <li>
                <strong>Try incognito/private mode</strong> - With only one
                wallet enabled
              </li>
              <li>
                <strong>Refresh after disabling</strong> - Changes take effect
                after page refresh
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WalletConflictWarning;
